import React from 'react';
import { Quaternion, Vector3 } from 'three';

import { Point2D, Point3D } from '@paragon/contract-library';
import { LineSegment2D, Vector2D } from '@paragon/geometry-library';
import { RenderOrder, Segment3D } from '@paragon/three-shared';

import { poundToInchScalar } from '../../library/unitScalarsForVisuals';
import { reverseShiftSegment } from '../../library/utilities/reverseShiftSegment';
import { SegmentLabel } from '../util/SegmentLabel';
import { VectorVisual, VectorVisualType } from '../util/VectorVisual';

interface Props {
  readonly linkSegment: Segment3D;
  readonly globalForceVector: Point2D;
  readonly axialForce: number;
  readonly transverseForce: number;
  readonly position: Point3D;
}

export function AxialTransverseLoadBox({
  linkSegment,
  globalForceVector,
  axialForce,
  transverseForce,
  position,
}: Props) {
  let forceSegment: Segment3D = {
    start: { x: 0, y: 0, z: 0 },
    end: { x: globalForceVector.x * poundToInchScalar, y: globalForceVector.y * poundToInchScalar, z: 0 },
  };
  let vectorVisualType;
  let oppositeCorner = forceSegment.end;
  if (axialForce < 0) {
    vectorVisualType = VectorVisualType.Blue;
    forceSegment = reverseShiftSegment(forceSegment);
    oppositeCorner = forceSegment.start;
  } else {
    vectorVisualType = VectorVisualType.Yellow;
  }

  const vectorToOppositeCorner = Vector2D.fromOriginTo(oppositeCorner);
  const axialDirection = LineSegment2D.fromSegmentObject(linkSegment).direction;
  const transverseDirection = axialDirection.rotateQuarterTurn();
  const axialComponentToCorner = vectorToOppositeCorner.getComponentInDirection(axialDirection);
  const axialSegmentLength = Math.abs(axialComponentToCorner);
  const transverseComponentToCorner = vectorToOppositeCorner.getComponentInDirection(transverseDirection);
  const transverseSegmentLength = Math.abs(transverseComponentToCorner);

  return (
    <object3D position={[position.x, position.y, position.z]}>
      <VectorVisual segment={forceSegment} thickness={0.2} vectorVisualType={vectorVisualType} />
      <object3D
        quaternion={new Quaternion().setFromUnitVectors(
          new Vector3(0, 1, 0),
          new Vector3(axialDirection.x, axialDirection.y),
        )}
      >
        {transverseSegmentLength > 0.1 ? (
          <>
            <mesh
              renderOrder={RenderOrder.AxialAndTransverseLoadBox}
              scale={[1, transverseSegmentLength, 1]}
              position={[-transverseComponentToCorner / 2, axialComponentToCorner, 0]}
              quaternion={new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), Math.PI / 2)}
            >
              <cylinderGeometry args={[0.02, 0.02, 1, 8, 1, false]} />
              <meshBasicMaterial color={0xde61ff} depthTest={false} />
            </mesh>
            <mesh
              renderOrder={RenderOrder.AxialAndTransverseLoadBox}
              scale={[1, transverseSegmentLength, 1]}
              position={[-transverseComponentToCorner / 2, 0, 0]}
              quaternion={new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), Math.PI / 2)}
            >
              <cylinderGeometry args={[0.02, 0.02, 1, 8, 1, false]} />
              <meshBasicMaterial color={0xde61ff} depthTest={false} />
            </mesh>
            <SegmentLabel
              segment={{
                start: { x: 0, y: 0, z: 0 },
                end: { x: transverseDirection.x, y: transverseDirection.y, z: 0 },
              }}
              text={transverseForce.toFixed(0)}
              position={[-transverseComponentToCorner / 2, axialComponentToCorner, 0]}
            />
          </>
        ) : null}
        {axialSegmentLength > 0.1 ? (
          <>
            <mesh
              renderOrder={RenderOrder.AxialAndTransverseLoadBox}
              scale={[1, axialSegmentLength, 1]}
              position={[0, axialComponentToCorner / 2, 0]}
            >
              <cylinderGeometry args={[0.02, 0.02, 1, 8, 1, false]} />
              <meshBasicMaterial color={0xde61ff} depthTest={false} />
            </mesh>
            <mesh
              renderOrder={RenderOrder.AxialAndTransverseLoadBox}
              scale={[1, axialSegmentLength, 1]}
              position={[-transverseComponentToCorner, axialComponentToCorner / 2, 0]}
            >
              <cylinderGeometry args={[0.02, 0.02, 1, 8, 1, false]} />
              <meshBasicMaterial color={0xde61ff} depthTest={false} />
            </mesh>
            <SegmentLabel
              segment={{
                start: { x: axialDirection.x, y: axialDirection.y, z: 0 },
                end: { x: 0, y: 0, z: 0 },
              }}
              text={axialForce.toFixed(0)}
              position={[-transverseComponentToCorner, axialComponentToCorner / 2, 0]}
            />
          </>
        ) : null}
      </object3D>
    </object3D>
  );
}
